import { environment } from '@environments/environment';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { PreloadAllModules, Route, RouterModule } from '@angular/router';

import { CustomerFormsService } from '@views/customer-forms/customer-forms.service';
import { CustomerFormsStore } from '@views/customer-forms/customer-forms.store';
import { CREATE_ENTITY_FLOW_ROUTE } from '@views/investor/investor.helpers';
import { initialLoginGuard } from '@views/login/initial-login/initial-login.guard';
import { PROJECT_INVITE_PATH } from '@views/login/project-invite/project-invite.path';
import { AuthGuard, LoggedInGuard } from '@views/misc/auth/auth.guard';
import { EnumResolver } from '@views/misc/enums/enums.resolver';

import { LinkResolver } from '@core/utilities/link/link-resolver.service';
import { NumberParamGuardResolver } from '@core/utilities/routes/number-param-guard-resolver.service';

import { CustomerFormInterceptor } from './views/customer-forms/customer-forms.interceptor';

/** Routes for the main entry point for this Squirrel application. */
const routes: Route[] = [
  // * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
  // Login.  * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
  // * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
  {
    path: 'login',
    data: { title: '' },
    loadChildren: () => import('@views/login/login.module').then(m => m.LoginModule),
  },
  {
    path: 'register',
    data: { title: 'Join Squirrel in less than 5 minutes' },
    loadChildren: () => import('@views/login/register/register.module').then(m => m.LoginRegisterModule),
  },
  {
    path: 'forgot-password',
    data: { title: 'Forgot password' },
    canActivate: [LoggedInGuard],
    loadComponent: () =>
      import('@views/login/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
  },
  {
    path: 'reset-password/:userId',
    data: { title: 'Reset password' },
    resolve: {
      validParams: NumberParamGuardResolver,
    },
    loadComponent: () =>
      import('@views/login/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
  },
  {
    path: 'account/initiallogin',
    data: { title: 'Set password' },
    canActivate: [initialLoginGuard],
    loadComponent: () =>
      import('@views/login/initial-login/initial-login.component').then(m => m.InitialLoginComponent),
  },
  {
    path: 'account/confirmemail',
    data: { title: 'Verify email' },
    loadComponent: () =>
      import('@views/login/confirm-email/confirm-email.component').then(m => m.ConfirmEmailComponent),
  },
  {
    // Redeems a construction-project invitation.
    path: PROJECT_INVITE_PATH,
    data: { title: 'Your construction project' },
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('@views/login/project-invite/project-invite.component').then(m => m.ProjectInviteComponent),
  },

  {
    path: 'investor-entity-member',
    loadChildren: () =>
      import('@views/investor-entity-member/investor-entity-member.module').then(m => m.ValidateIdModule),
  },

  // * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
  // Customer forms. * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
  // * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
  {
    path: 'customer',
    providers: [
      CustomerFormsStore,
      CustomerFormsService,
      provideHttpClient(withInterceptorsFromDi()),
      { provide: HTTP_INTERCEPTORS, useClass: CustomerFormInterceptor, multi: true },
    ],
    loadChildren: () => import('./views/customer-forms/routes').then(mod => mod.CUSTOMER_FORM_ROUTES),
  },
  // Redirect for old doc collection links.
  {
    path: 'customer-forms',
    redirectTo: 'customer',
  },
  // Redirect for Legacy OLA routes
  {
    path: 'application',
    redirectTo: 'customer',
  },

  // * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
  // Gated Content.  * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
  // ⚠️ Users must be authenticated to access! * * * * * * * * * * * * * * * * *
  // * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *

  {
    path: '',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('@views/authorized-views/authorized-views.component').then(x => x.AuthorizedViewsComponent),
    children: [
      /** Settings: */
      {
        path: 'settings',
        pathMatch: 'prefix',
        canActivate: [AuthGuard],
        resolve: { enums: EnumResolver },
        loadChildren: () => import('@views/settings/settings.module').then(m => m.SettingsModule),
      },

      /** Borrower: */
      {
        path: 'borrower',
        canActivate: [AuthGuard],
        resolve: { enums: EnumResolver },
        loadChildren: () => import('@views/borrower/borrower.module').then(m => m.BorrowerModule),
      },

      /** Investor: */
      {
        path: 'investor',
        canActivate: [AuthGuard],
        resolve: { enums: EnumResolver },
        loadChildren: () => import('@views/investor/investor.module').then(m => m.InvestorModule),
      },

      /** Profile: */
      {
        path: 'profile',
        data: { title: 'Profile' },
        canActivate: [AuthGuard],
        resolve: { enums: EnumResolver },
        children: [
          {
            path: 'incomplete',
            loadComponent: () =>
              import('@views/profile/profile-incomplete.component').then(c => c.ProfileIncompleteComponent),
          },
          {
            path: 'validate-id',
            loadChildren: () => import('@views/profile/validate-id/validate-id.module').then(m => m.ValidateIdModule),
          },
        ],
      },
      // Alternate version for profile pages used when part of the create entity process
      {
        path: CREATE_ENTITY_FLOW_ROUTE,
        // createEntity flag to show that user is in create entity process
        data: { title: 'Create entity', createEntity: true },
        children: [
          {
            path: 'incomplete',
            loadComponent: () =>
              import('@views/profile/profile-incomplete.component').then(c => c.ProfileIncompleteComponent),
          },
          {
            path: 'validate-id',
            loadChildren: () => import('@views/profile/validate-id/validate-id.module').then(m => m.ValidateIdModule),
          },
        ],
      },

      // Path that will trigger a specified dialog to open then redirect background to the dashboard
      {
        path: 'open/:dialogName',
        canActivate: [AuthGuard],
        loadComponent: () => import('@views/misc/sidenav/open-dialog.component').then(c => c.OpenDialogComponent),
      },

      {
        path: '',
        pathMatch: 'full',
        data: { title: '' },
        canActivate: [AuthGuard],
        resolve: { enums: EnumResolver },
        loadChildren: () => import('@views/dashboard/dashboard.module').then(m => m.DashboardModule),
      },
    ],
  },

  {
    path: 'link',
    resolve: { invalidLink: LinkResolver },
    loadChildren: () => import('@views/misc/scenario/invalid-link/invalid-link.module').then(m => m.InvalidLinkModule),
  },

  /** 404: */
  {
    path: '**',
    redirectTo: '',
  },
];

/**
 * `import` dependancies for `AppRoutingModule`.
 */
@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
      enableTracing: environment.logRoutes,
      scrollPositionRestoration: 'enabled',
      paramsInheritanceStrategy: 'always',
      // anchorScrolling added to enable this workaround https://github.com/angular/angular/issues/26744#issuecomment-1735808773
      // to prevent scrolling on ola navigation.
      anchorScrolling: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
